import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SearchPlatform } from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import {
  getDefaultAccountFilters,
  NodeFilters,
  useFilterContext,
} from '@/context/FilterProvider';

const dbtFilters: (keyof NodeFilters)[] = [
  'access',
  'environmentTypes',
  'materialization',
  'modelingLayer',
  'projects',
  'resourceType',
  'tags',
];
const warehouseFilters: (keyof NodeFilters)[] = ['warehouseAssetTypes'];

export const useAccountFilterPanel = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const hasSetDefaultFilters = useRef(false);

  const [params, setSearchParams] = useSearchParams();
  const filterContext = useFilterContext();

  // if no filter params are set, set the default filters only once
  useEffect(() => {
    if (params.size === 0 && !hasSetDefaultFilters.current) {
      const defaultFilter = JSON.stringify(
        Object.keys(filterContext.filters).length > 0
          ? filterContext.filters
          : getDefaultAccountFilters(hasAccountFeature('catalogIngestion')),
      );
      setSearchParams((prev) => {
        prev.set('filters', defaultFilter);
        return prev;
      });
      hasSetDefaultFilters.current = true;
    }
  }, [filterContext.filters, hasAccountFeature, params.size, setSearchParams]);

  // if a higher-level filter is removed, remove the associated filters
  const dbtIsActive = !!filterContext.filters.platforms?.includes(SearchPlatform.Dbt);
  const warehouseIsActive = !!filterContext.filters.platforms?.includes(
    SearchPlatform.Snowflake,
  );
  useEffect(() => {
    const filters = { ...filterContext.filters };
    let removedPlatformFilters = false;
    if (!dbtIsActive) {
      dbtFilters.forEach((filter) => {
        if (filters[filter] !== undefined) {
          delete filters[filter];
          removedPlatformFilters = true;
        }
      });
    }
    if (!warehouseIsActive) {
      warehouseFilters.forEach((filter) => {
        if (filters[filter] !== undefined) {
          delete filters[filter];
          removedPlatformFilters = true;
        }
      });
    }
    if (removedPlatformFilters) {
      setSearchParams((prev) => {
        prev.set('filters', JSON.stringify(filters));
        return prev;
      });
    }
  }, [dbtIsActive, warehouseIsActive, setSearchParams, filterContext.filters]);

  return {
    dbtIsActive,
    warehouseIsActive,
  };
};
